import * as React from 'react'
import {
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  CircularProgress,
  TableSortLabel,
  Button,
  Grid,
  Box,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { useStyles } from 'common/styles'
import { useApplications } from './useApplications'
import { transformRowsForTable } from './helpers'
import { columns } from './config'
import ApplicationDrawer from './ApplicationDrawer'
import ResizableTableCell from './ResizableTableCell'
import AddIcon from '@mui/icons-material/Add'

export const Applications = () => {
  const theme = useTheme()
  const classes = useStyles()
  const {
    loading,
    availableTags,
    rows,
    page,
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage,
    order,
    orderBy,
    handleRequestSort,
    drawerOpen,
    handleDrawerClose,
    handleStatusChange,
    selectedRow,
    handleRowClick,
    statuses,
    handleFavoriteChange,
    handleDelete,
    candidateId,
    setDrawerOpen,
    setSelectedRow,
    userId,
  } = useApplications()

  const formattedRows = transformRowsForTable({
    rows,
    onUpdateStatus: handleStatusChange,
    statuses,
    handleFavoriteChange,
    handleDelete,
    candidateId,
    userId,
  })

  const getRawRow = selectedFormattedRow => {
    return rows.find(row => row.id === selectedFormattedRow.id)
  }

  const addNewApplication = () => {
    setSelectedRow(null)
    setDrawerOpen(true)
  }

  return loading ? (
    <CircularProgress className={classes.submitProgress} />
  ) : (
    <Box sx={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
      <Paper 
        elevation={0} 
        sx={{ 
          width: '100%', 
          mb: 4,
          backgroundColor: theme.palette.background.dark,
          borderRadius: '0px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
          mx: 0
        }}
      >
        <Box sx={{ maxWidth: '91.666667%', mx: 'auto', p: { xs: 2, md: 3 } }}>
          <Grid container spacing={0} sx={{ px: 0 }}>
            <Grid item xs={12} md={8} sx={{ pl: 0 }}>
              <Typography variant='h5' sx={{ 
                color: 'white',
                fontWeight: 'bold',
                mb: 1,
                pl: 0
              }}>
                Track Your Job Applications
              </Typography>
              <Typography variant='caption' sx={{ 
                color: 'rgba(255,255,255,0.7)',
                display: 'block',
                mb: { xs: 2, md: 0 },
                whiteSpace: 'normal',
                wordBreak: 'normal',
                pl: 0
              }}>
                Keep track of your job applications, organize them with tags, and monitor your progress. 
                Add jobs from MyStethi or external sources to streamline your job search process.
              </Typography>
              <Typography variant='caption' sx={{ 
                color: 'rgba(255, 255, 255, 0.7)',
                mb: { xs: 2, md: 0 },
                whiteSpace: 'normal',
                wordBreak: 'normal',
                pl: 0,
                fontSize: '0.75rem'
              }}>
                Note: This is not submitted to the hospital, this is just a tool to help you track job applications you have submitted externally.
              </Typography>
            </Grid>
            <Grid 
              item 
              xs={12} 
              md={4} 
              sx={{ 
                display: 'flex', 
                alignItems: { xs: 'flex-start', md: 'center' },
                justifyContent: { xs: 'flex-start', md: 'flex-end' },
                pr: 0
              }}
            >
              <Button 
                variant='contained'
                color='primary'
                onClick={addNewApplication}
                startIcon={<AddIcon />}
                sx={{ 
                  backgroundColor: 'white',
                  color: 'primary.main',
                  '&:hover': {
                    backgroundColor: 'grey.100'
                  }
                }}
              >
                Add New Application
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Paper>
      <Grid container item xs={12} justifyContent='center'>
        <Grid container item xs={11}>
          <Paper 
            sx={{ 
              width: '100%', 
              marginBottom: '50px',
              '& .MuiTableHead-root': {
                '& .MuiTableCell-root': {
                  backgroundColor: 'background.paper',
                  borderBottom: '2px solid',
                  borderColor: 'divider',
                  py: 2
                }
              },
              '& .MuiTableBody-root': {
                '& .MuiTableRow-root': {
                  '&:hover': {
                    backgroundColor: 'action.hover'
                  },
                  '& .MuiTableCell-root': {
                    py: 2,
                    borderBottom: '1px solid',
                    borderColor: 'divider'
                  }
                }
              }
            }} 
            className={classes.tablePaper}
          >
            <TableContainer className={classes.tableContainer}>
              <Table stickyHeader aria-label='sticky table'>
                <TableHead>
                  <TableRow>
                    {columns.map(column => (
                      <ResizableTableCell
                        key={column.id}
                        align={column.align}
                        isHeader={true}
                        fitheader={column.fitheader}
                        sortDirection={orderBy === column.id ? order : false}
                        sx={{
                          '&.MuiTableCell-stickyHeader': {
                            backgroundColor: 'background.paper',
                          },
                          '& .MuiTableSortLabel-root': {
                            '&:hover': {
                              color: 'primary.main'
                            },
                            '&.MuiTableSortLabel-active': {
                              color: 'primary.main',
                              '& .MuiTableSortLabel-icon': {
                                color: 'primary.main'
                              }
                            }
                          }
                        }}
                      >
                        {column.sortable ? (
                          <TableSortLabel
                            active={orderBy === column.id}
                            direction={orderBy === column.id ? order : 'asc'}
                            onClick={event => handleRequestSort(event, column.id)}
                          >
                            <Typography variant='subtitle2' sx={{ fontWeight: 600 }}>
                              {column.label}
                            </Typography>
                          </TableSortLabel>
                        ) : (
                          <Typography variant='subtitle2' sx={{ fontWeight: 600 }}>
                            {column.label}
                          </Typography>
                        )}
                      </ResizableTableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {formattedRows
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map(row => (
                      <TableRow
                        hover
                        role='checkbox'
                        tabIndex={-1}
                        key={row.id}
                        onClick={() => handleRowClick(getRawRow(row))}
                        sx={{ 
                          cursor: 'pointer',
                          '&:last-child td': { borderBottom: 0 }
                        }}
                      >
                        {columns.map(column => {
                          const value = column.id === 'status' ? row.statusDropdown : row[column.id]
                          return (
                            <ResizableTableCell
                              key={column.id}
                              align={column.align}
                              sx={{ 
                                cursor: 'pointer',
                                '& .MuiChip-root': {
                                  height: '24px'
                                }
                              }}
                            >
                              {value}
                            </ResizableTableCell>
                          )
                        })}
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[10, 25, 100]}
              component='div'
              count={rows.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{
                borderTop: '1px solid',
                borderColor: 'divider'
              }}
            />
            <ApplicationDrawer
              open={drawerOpen}
              onClose={handleDrawerClose}
              selectedRow={selectedRow}
              statuses={statuses}
              availableTags={availableTags}
            />
          </Paper>
        </Grid>
      </Grid>
    </Box>
  )
}

export default Applications
